/**
 * Shared country phone codes constant
 * Used across multiple components for phone number input with country selection
 */

export interface CountryPhoneCode {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
}

export const COUNTRY_PHONE_CODES: CountryPhoneCode[] = [
  { code: 'MA', name: 'Maroc', dialCode: '+212', flag: '🇲🇦' },
  { code: 'DZ', name: 'Algérie', dialCode: '+213', flag: '🇩🇿' },
  { code: 'TN', name: 'Tunisie', dialCode: '+216', flag: '🇹🇳' },
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷' },
  { code: 'ES', name: 'Espagne', dialCode: '+34', flag: '🇪🇸' },
  { code: 'BE', name: 'Belgique', dialCode: '+32', flag: '🇧🇪' },
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦' },
  { code: 'US', name: 'États-Unis', dialCode: '+1', flag: '🇺🇸' },
  { code: 'GB', name: 'Royaume-Uni', dialCode: '+44', flag: '🇬🇧' },
  { code: 'DE', name: 'Allemagne', dialCode: '+49', flag: '🇩🇪' },
  { code: 'IT', name: 'Italie', dialCode: '+39', flag: '🇮🇹' },
  { code: 'NL', name: 'Pays-Bas', dialCode: '+31', flag: '🇳🇱' },
  { code: 'PT', name: 'Portugal', dialCode: '+351', flag: '🇵🇹' },
  { code: 'SA', name: 'Arabie Saoudite', dialCode: '+966', flag: '🇸🇦' },
  { code: 'AE', name: 'Émirats Arabes Unis', dialCode: '+971', flag: '🇦🇪' },
  { code: 'EG', name: 'Égypte', dialCode: '+20', flag: '🇪🇬' },
  { code: 'SN', name: 'Sénégal', dialCode: '+221', flag: '🇸🇳' },
  { code: 'CI', name: "Côte d'Ivoire", dialCode: '+225', flag: '🇨🇮' },
  { code: 'ML', name: 'Mali', dialCode: '+223', flag: '🇲🇱' },
  { code: 'MR', name: 'Mauritanie', dialCode: '+222', flag: '🇲🇷' },
];

export const DEFAULT_PHONE_COUNTRY = 'MA';

/**
 * Record format of country phone codes (for lookup by code)
 * Used in components that need quick access by country code
 */
export const COUNTRY_PHONE_CODES_MAP: Record<string, { dialCode: string; flag: string }> = {
  'MA': { dialCode: '+212', flag: '🇲🇦' },
  'DZ': { dialCode: '+213', flag: '🇩🇿' },
  'TN': { dialCode: '+216', flag: '🇹🇳' },
  'FR': { dialCode: '+33', flag: '🇫🇷' },
  'ES': { dialCode: '+34', flag: '🇪🇸' },
  'BE': { dialCode: '+32', flag: '🇧🇪' },
  'CA': { dialCode: '+1', flag: '🇨🇦' },
  'US': { dialCode: '+1', flag: '🇺🇸' },
  'GB': { dialCode: '+44', flag: '🇬🇧' },
  'DE': { dialCode: '+49', flag: '🇩🇪' },
  'IT': { dialCode: '+39', flag: '🇮🇹' },
  'NL': { dialCode: '+31', flag: '🇳🇱' },
  'PT': { dialCode: '+351', flag: '🇵🇹' },
  'SA': { dialCode: '+966', flag: '🇸🇦' },
  'AE': { dialCode: '+971', flag: '🇦🇪' },
  'EG': { dialCode: '+20', flag: '🇪🇬' },
  'SN': { dialCode: '+221', flag: '🇸🇳' },
  'CI': { dialCode: '+225', flag: '🇨🇮' },
  'ML': { dialCode: '+223', flag: '🇲🇱' },
  'MR': { dialCode: '+222', flag: '🇲🇷' },
};

/**
 * Helper function to find a country by its code
 */
export const getCountryByCode = (code: string): CountryPhoneCode | undefined => {
  return COUNTRY_PHONE_CODES.find((country) => country.code === code);
};

/**
 * Helper function to find a country by its dial code
 */
export const getCountryByDialCode = (dialCode: string): CountryPhoneCode | undefined => {
  return COUNTRY_PHONE_CODES.find((country) => country.dialCode === dialCode);
};

/**
 * Format phone number with country code
 * @param phone - Phone number
 * @param countryCode - Country code (defaults to 'MA')
 * @returns Formatted phone string with flag and dial code
 */
export const formatPhoneWithCountry = (phone?: string, countryCode?: string): string => {
  if (!phone) return '';

  // If phone already starts with + or 00, it already has the dial code - don't add another
  if (phone.startsWith('+') || phone.startsWith('00')) {
    // Try to find the country flag based on the dial code in the phone
    for (const [, data] of Object.entries(COUNTRY_PHONE_CODES_MAP)) {
      if (phone.startsWith(data.dialCode)) {
        return `${data.flag} ${phone}`;
      }
    }
    return phone;
  }

  // Default to Morocco (MA) if no country code is provided
  const effectiveCountryCode = countryCode || 'MA';
  const country = COUNTRY_PHONE_CODES_MAP[effectiveCountryCode];
  if (country) {
    return `${country.flag} ${country.dialCode} ${phone}`;
  }
  return phone;
};
